import React, { useState } from 'react';
import { XMarkIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';

interface QuantityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (quantity: number) => void;
  productName: string;
  isLoading?: boolean;
}

const QuantityModal: React.FC<QuantityModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  productName, 
  isLoading = false 
}) => {
  const [quantityInput, setQuantityInput] = useState<string>('1');
  const [quantityError, setQuantityError] = useState<string>('');

  const validateQuantity = (value: string): string => {
    // Check if empty
    if (!value.trim()) {
      return 'Quantity is required';
    }
    
    // Check if it's a valid number
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Please enter a valid number';
    }
    
    // Check if it's a positive integer
    if (!Number.isInteger(num) || num <= 0) {
      return 'Please enter a positive integer (whole number greater than 0)';
    }
    
    return '';
  };

  const handleQuantityChange = (value: string) => {
    setQuantityInput(value);
    const error = validateQuantity(value);
    setQuantityError(error);
  };

  const incrementQuantity = () => {
    const current = parseInt(quantityInput) || 1;
    const newValue = (current + 1).toString();
    handleQuantityChange(newValue);
  };

  const decrementQuantity = () => {
    const current = parseInt(quantityInput) || 1;
    if (current > 1) {
      const newValue = (current - 1).toString();
      handleQuantityChange(newValue);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateQuantity(quantityInput);
    if (error) {
      setQuantityError(error);
      return;
    }
    
    const parsedQuantity = parseInt(quantityInput);
    if (parsedQuantity > 0) {
      onConfirm(parsedQuantity);
    }
  };

  const handleClose = () => {
    setQuantityInput('1');
    setQuantityError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-secondary-400 hover:text-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={handleClose}
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-secondary-900">
                Match Tags to Product
              </h3>
              
              <div className="mt-4">
                <p className="text-sm text-secondary-600 mb-2">Product:</p>
                <p className="text-sm font-medium text-secondary-900 bg-secondary-50 p-2 rounded">
                  {productName}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="mt-6">
                <div>
                  <label htmlFor="quantity" className="block text-sm font-medium text-secondary-700 mb-2">
                    How many items would you like to associate to tags?
                  </label>
                  
                  <div>
                    <div className="flex items-center justify-center space-x-4">
                      <button
                        type="button"
                        onClick={decrementQuantity}
                        disabled={parseInt(quantityInput) <= 1 || isLoading}
                        className="p-2 rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      
                      <input
                        id="quantity"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={quantityInput}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        className={`w-20 text-center px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          quantityError
                            ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                            : 'border-secondary-300 focus:ring-primary-500 focus:border-primary-500'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        placeholder="1"
                        disabled={isLoading}
                      />
                      
                      <button
                        type="button"
                        onClick={incrementQuantity}
                        disabled={isLoading}
                        className="p-2 rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {quantityError && (
                      <p className="mt-2 text-sm text-error-500 text-center">{quantityError}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="flex-1 inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!quantityInput || !!quantityError || isLoading}
                    className="flex-1 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:text-sm"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Continue'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuantityModal;